import { AdminAbility } from '@repo/sdk/backoffice-abilities'
import { backofficeAuthSignOut } from '@repo/sdk/gen'
import { useSidebar } from '@repo/ui/aceternity/sidebar'
import { Avatar, AvatarFallback } from '@repo/ui/avatar'
import { Button } from '@repo/ui/button'
import { ThemePicker } from '@repo/ui/commons/theme-picker'
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger
} from '@repo/ui/drawer'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@repo/ui/dropdown-menu'
import { useIsMobile } from '@repo/ui/hooks/use-mobile'
import { getInitial } from '@repo/ui/utils'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
	ChevronsUpDown,
	LogOut,
	Settings2,
	ShieldCheck,
	UserRound
} from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { adminSessionQueryOptions, meQueryOptions } from '@/lib/query-options'
import { Can } from '@/modules/permissions/components/can'

function AdminNavUser() {
	const { setOpen } = useSidebar()
	const isMobile = useIsMobile()
	const { t } = useTranslation()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { data: user } = useSuspenseQuery(meQueryOptions())

	const handleLogout = async () => {
		await backofficeAuthSignOut({ body: {} })
		// Mark the session signed out before navigating: the (auth) gate reads it
		// cache-first, so a stale authenticated entry would bounce the logout back
		// to the app. Clearing the whole cache here instead would suspend the
		// private shell, user menu included, while the login page loads.
		queryClient.setQueryData(adminSessionQueryOptions().queryKey, {
			sessionType: 'unauthenticated'
		})
		await navigate({ to: '/login' })
		queryClient.clear()
	}

	const closeMobileSidebar = () => {
		if (isMobile) {
			setOpen(false)
		}
	}

	const initial = getInitial(user.name || user.email || '')

	const userAvatar = (
		<Avatar className='size-8 rounded-md after:rounded-md'>
			<AvatarFallback className='rounded-md bg-background font-medium text-foreground'>
				{initial}
			</AvatarFallback>
		</Avatar>
	)

	// Avatar, name and address in a row that fades to the avatar alone on the
	// closed rail, like the nav links above it.
	const trigger = (
		<>
			{userAvatar}
			<div className='grid min-w-0 flex-1 text-left leading-tight transition-opacity duration-200 group-data-[state=closed]/sidebar:opacity-0'>
				<span
					className='truncate font-medium text-foreground text-sm'
					title={user.name ?? undefined}
				>
					{user.name}
				</span>
				<span
					className='truncate text-muted-foreground text-xs'
					title={user.email ?? undefined}
				>
					{user.email}
				</span>
			</div>
			<ChevronsUpDown className='ml-auto size-4 shrink-0 text-muted-foreground transition-opacity duration-200 group-data-[state=closed]/sidebar:opacity-0' />
		</>
	)

	if (isMobile) {
		return (
			<Drawer>
				<DrawerTrigger asChild>
					<button
						aria-label={t('common:app_sidebar.user_menu.open')}
						className={TRIGGER}
						type='button'
					>
						{trigger}
					</button>
				</DrawerTrigger>
				<DrawerContent>
					<DrawerHeader className='border-b'>
						<div className='flex items-center gap-3 text-left'>
							{userAvatar}
							<div className='grid min-w-0 flex-1 leading-tight'>
								<DrawerTitle className='truncate text-sm'>
									{user.name}
								</DrawerTitle>
								<DrawerDescription className='truncate text-xs'>
									{user.email}
								</DrawerDescription>
							</div>
						</div>
					</DrawerHeader>
					<div className='flex flex-col gap-1 p-2'>
						<DrawerClose asChild>
							<Link
								className='flex h-11 items-center gap-3 rounded-md px-3 font-normal text-sm hover:bg-muted'
								onClick={closeMobileSidebar}
								preload={false}
								to='/profile'
							>
								<UserRound className='size-4' />
								<span>
									{t('common:app_sidebar.menu.profile')}
								</span>
							</Link>
						</DrawerClose>
						<DrawerClose asChild>
							<Link
								className='flex h-11 items-center gap-3 rounded-md px-3 font-normal text-sm hover:bg-muted'
								onClick={closeMobileSidebar}
								to='/configuration'
							>
								<Settings2 className='size-4' />
								<span>{t('po:config.page_title')}</span>
							</Link>
						</DrawerClose>
						<Can permissions={[AdminAbility.AdminsManage]}>
							<DrawerClose asChild>
								<Link
									className='flex h-11 items-center gap-3 rounded-md px-3 font-normal text-sm hover:bg-muted'
									onClick={closeMobileSidebar}
									to='/admins'
								>
									<ShieldCheck className='size-4' />
									<span>{t('admin-admins:page_title')}</span>
								</Link>
							</DrawerClose>
						</Can>
						<ThemePicker />
						<DrawerClose asChild>
							<Button
								className='h-11 justify-start gap-3 px-3 font-normal'
								onClick={handleLogout}
								variant='ghost'
							>
								<LogOut />
								<span>
									{t('common:app_sidebar.menu.logout')}
								</span>
							</Button>
						</DrawerClose>
					</div>
				</DrawerContent>
			</Drawer>
		)
	}

	const dropdownContent = (
		<DropdownMenuContent
			align='start'
			className='w-56'
			side='top'
			sideOffset={8}
		>
			<div className='flex items-center gap-2 p-2'>
				{userAvatar}
				<div className='grid min-w-0 flex-1 text-left text-sm leading-tight'>
					<span className='truncate font-medium text-foreground'>
						{user.name}
					</span>
					<span className='truncate text-muted-foreground text-xs'>
						{user.email}
					</span>
				</div>
			</div>
			<DropdownMenuSeparator />
			<DropdownMenuItem
				render={
					<Link preload={false} to='/profile'>
						<UserRound />
						<span>{t('common:app_sidebar.menu.profile')}</span>
					</Link>
				}
			/>
			<DropdownMenuItem
				render={
					<Link to='/configuration'>
						<Settings2 />
						<span>{t('po:config.page_title')}</span>
					</Link>
				}
			/>
			<Can permissions={[AdminAbility.AdminsManage]}>
				<DropdownMenuItem
					render={
						<Link to='/admins'>
							<ShieldCheck />
							<span>{t('admin-admins:page_title')}</span>
						</Link>
					}
				/>
			</Can>
			<DropdownMenuSeparator />
			<ThemePicker />
			<DropdownMenuSeparator />
			<DropdownMenuItem onClick={handleLogout}>
				<LogOut />
				<span>{t('common:app_sidebar.menu.logout')}</span>
			</DropdownMenuItem>
		</DropdownMenuContent>
	)

	return (
		<DropdownMenu>
			<DropdownMenuTrigger
				aria-label={t('common:app_sidebar.user_menu.open')}
				className={TRIGGER}
			>
				{trigger}
			</DropdownMenuTrigger>
			{dropdownContent}
		</DropdownMenu>
	)
}

const TRIGGER =
	'flex h-11 w-full shrink-0 items-center gap-2.5 rounded-md px-1 outline-none transition-colors hover:bg-sidebar-accent focus-visible:outline-2 focus-visible:outline-sidebar-ring aria-expanded:bg-sidebar-accent'

export { AdminNavUser }
